import React, { useState, useEffect } from "react";
import {Row, Button, Modal, Form, notification} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";

import { URLS } from "../../constants";
import { useModal } from "../../hooks";
import { RegularExpressionForm } from "../../components/Forms";
import { ContentInner, StyledTable } from "../../pages/styled";

export default function RegularExpressions({ token, environment }) {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [newRegularExpression, setNewRegularExpression] = useState({});
  const [currentRegularExpression, setCurrentRegularExpression] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);

  const initialValues = { name: "", description: "" };
  const [isModalShown, setModalShown, onModalOkAction, onModalCancelAction] =
    useModal();
  const [
    isEditModalShown,
    setEditModalShown,
    onEditModalOkAction,
    onEditModalCancelAction,
  ] = useModal();
  const [
    isDeleteModalShown,
    setDeleteModalShown,
    onDeleteModalOkAction,
    onDeleteModalCancelAction,
  ] = useModal();

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "center",
      with: 250,
    },
    {
      title: "",
      dataIndex: "edit",
      key: "edit",
      width: 72,
      render: (_, row) => {
        return (
          <Button
            type="link"
            size="small"
            onClick={() => {
              onEditRegularExpression(row);
            }}
          >
            Edit
          </Button>
        );
      },
    },
    {
      title: "",
      dataIndex: "delete",
      key: "delete",
      width: 88,
      render: (_, row) => {
        return (
          <Button
            danger
            type="link"
            size="small"
            onClick={() => {
              setItemToDelete(row);
              setDeleteModalShown()();
            }}
          >
            Delete
          </Button>
        );
      },
    },
  ];

  const getRegularExpressions = async () => {
    axios
      .get(URLS.FETCH_REGULAR_EXPRESSIONS, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          env: environment,
        },
      })
      .then((res) => {
        setData(res.data);
      });
  };

  const deleteRegularExpressionById = async () => {
    return axios
      .delete(`${URLS.FETCH_REGULAR_EXPRESSIONS}/${itemToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          env: environment,
        },
      })
      .then((res) => res.data);
  };

  const onDelete = async () => {
    try {
      const response = await deleteRegularExpressionById();

      if (response) {
        const newData = data.filter((d) => {
          return d.id !== itemToDelete.id;
        });

        setData(newData);
      }
    } catch (e) {
      const message = e?.response?.data?.message || e?.message
      notification.error({
        message: 'Request error',
        description: message
      });
    }
  };

  const handleOk = () => {
    onDelete(itemToDelete);
    onDeleteModalOkAction()();
  };

  const onEditRegularExpression = (data) => {
    setCurrentRegularExpression({ ...data });
    setEditModalShown()();
  };

  const updateRegularExpression = (name, value, isNew) => {
    if (isNew) {
      setNewRegularExpression({ ...newRegularExpression, [name]: value });
    } else {
      setCurrentRegularExpression({ ...currentRegularExpression, [name]: value });
    }
  };

  const onInputChange = (isNew) => (e) => {
    const { name, value } = e.target;
    updateRegularExpression(name, value, isNew);
  };

  const resetCurrentRegularExpression = () => {
    setCurrentRegularExpression(initialValues);
  };

  const showCreateModal = () => {
    resetCurrentRegularExpression();
    setModalShown()();
  };

  const createRegularExpression = async () => (
    axios
    .post(URLS.FETCH_REGULAR_EXPRESSIONS, newRegularExpression, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        env: environment,
      },
    })
    .then((res) => res.data)
  );

  const editRegularExpression = async () => (
    axios
    .put(`${URLS.FETCH_REGULAR_EXPRESSIONS}/${currentRegularExpression.id}`, currentRegularExpression, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        env: environment,
      },
    })
    .then((res) => res.data)
  );

  const onFinishAdd = async () => {
    try {
      onModalOkAction()();
      const response = await createRegularExpression();

      if (response) {
        setNewRegularExpression({});
        setData([response, ...data])
      }
    } catch (e) {
      const message = e?.response?.data?.message || e?.message
      notification.error({
        message: 'Request error',
        description: message
      });
    }
  };

  const onFinishEdit = async () => {
    try {
      onEditModalOkAction()();
      const response = await editRegularExpression();

      if (response) {
        const newData = data.map((item) => {
          if (response.id === item.id) {
            return {...response};
          }
          return item;
        });
        setData(newData);
      }
    } catch (e) {
      const message = e?.response?.data?.message || e?.message
      notification.error({
        message: 'Request error',
        description: message
      });
    }
  };

  useEffect(() => {
    if (!data.length) {
      getRegularExpressions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  return (
    <>
      <Modal
        title="Delete regular expression"
        visible={isDeleteModalShown}
        onOk={handleOk}
        onCancel={onDeleteModalCancelAction()}
      >
        Are you sure you want to delete regular expression?
      </Modal>
      <Modal
        title="Create new regular expression"
        okText="Create regular expression"
        visible={isModalShown}
        onOk={() => {}}
        onCancel={() => {
          onModalCancelAction()();
        }}
        okButtonProps={{
          form: "regular-expression-create-form",
          key: "submit",
          htmlType: "submit",
        }}
      >
        <RegularExpressionForm
          form={form}
          onFinish={onFinishAdd}
          onInputChange={onInputChange(true)}
        />
      </Modal>

      <Modal
        title="Edit regular expression"
        okText="Save changes"
        visible={isEditModalShown}
        onOk={() => {}}
        onCancel={() => {
          onEditModalCancelAction()();
          setCurrentRegularExpression({});
        }}
        okButtonProps={{
          form: "regular-expression-edit-form",
          key: "submit",
          htmlType: "submit",
        }}
      >
        <RegularExpressionForm
          regularExpression={currentRegularExpression}
          isEdit={true}
          form={form}
          onFinish={onFinishEdit}
          onInputChange={onInputChange()}
        />
      </Modal>
      <ContentInner>
        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={() => {
            showCreateModal();
          }}
          style={{ marginBottom: "25px" }}
        >
          Add new regular expression
        </Button>
        <Row justify="center" width="100%">
          <StyledTable
            style={{ width: "100%" }}
            columns={columns}
            rowKey="id"
            dataSource={data}
            pagination={false}
            tableLayout="fixed"
          />
        </Row>
      </ContentInner>
    </>
  );
}
